export type SortDirection = 'asc' | 'desc';

export type SortConfig<K extends string = string> = {
  key: K;
  direction: SortDirection;
};

export type SortValueKind = 'string' | 'number' | 'date' | 'boolean' | 'severity';

const SEVERITY_RANK: Record<string, number> = {
  high: 3,
  medium: 2,
  low: 1,
};

export const toggleSortConfig = <K extends string>(
  prev: SortConfig<K>,
  key: K,
  defaultDirection: SortDirection = 'asc'
): SortConfig<K> => {
  if (prev.key === key) {
    return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { key, direction: defaultDirection };
};

export const defaultDirectionForKind = (kind: SortValueKind): SortDirection => {
  if (kind === 'date' || kind === 'severity') return 'desc';
  return 'asc';
};

const isEmpty = (value: unknown): boolean =>
  value === null || value === undefined || value === '';

const compareRaw = (
  a: unknown,
  b: unknown,
  kind: SortValueKind,
  direction: SortDirection
): number => {
  const aEmpty = isEmpty(a);
  const bEmpty = isEmpty(b);
  if (aEmpty && bEmpty) return 0;
  // Пустые значения всегда внизу, в обе стороны сортировки
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  let cmp = 0;
  switch (kind) {
    case 'number': {
      cmp = Number(a) - Number(b);
      break;
    }
    case 'date': {
      cmp = String(a).localeCompare(String(b));
      break;
    }
    case 'boolean': {
      cmp = Number(Boolean(a)) - Number(Boolean(b));
      break;
    }
    case 'severity': {
      const ar = SEVERITY_RANK[String(a)] ?? 0;
      const br = SEVERITY_RANK[String(b)] ?? 0;
      cmp = ar - br;
      break;
    }
    default: {
      cmp = String(a).localeCompare(String(b), 'ru', { sensitivity: 'base' });
    }
  }

  return direction === 'asc' ? cmp : -cmp;
};

export const sortByConfig = <T extends { id?: number | string }, K extends string>(
  rows: T[],
  config: SortConfig<K>,
  getValue: (row: T, key: K) => unknown,
  getKind: (key: K) => SortValueKind,
  secondary: SortConfig<K>[] = []
): T[] => {
  const sorted = [...rows];
  const keys = [config, ...secondary];
  sorted.sort((a, b) => {
    for (const c of keys) {
      const cmp = compareRaw(
        getValue(a, c.key),
        getValue(b, c.key),
        getKind(c.key),
        c.direction
      );
      if (cmp !== 0) return cmp;
    }
    return String(a.id ?? '').localeCompare(String(b.id ?? ''), 'en', {
      numeric: true,
    });
  });
  return sorted;
};

export const sortIndicator = <K extends string>(
  config: SortConfig<K>,
  key: K
): string => {
  if (config.key !== key) return '';
  return config.direction === 'asc' ? ' ↑' : ' ↓';
};
